import React, { useState, useCallback, useEffect, useRef } from "react";
import apiClient from "../api/ResturantClient";

const QRCodeScanning = () => {
  const restaurant_id = sessionStorage.getItem("restaurant_id");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const qrCanvasRef = useRef(null);
  const qrImageRef = useRef(null);

  useEffect(() => {
    handleGenerateQR();
  }, []);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
    <html>
      <head>
        <title>Print QR Code</title>
        <style>
          body {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            margin: 0;
            padding: 20px;
            box-sizing: border-box;
            font-family: Arial, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
          .print-container {
            background: white;
            padding: 40px;
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            text-align: center;
          }
          .qr-image {
            max-width: 300px;
            height: auto;
            margin-bottom: 20px;
            border: 2px solid #e5e7eb;
            border-radius: 12px;
          }
          .logo {
            width: 60px;
            height: 60px;
            object-fit: contain;
            margin-bottom: 10px;
          }
          .brand {
            font-size: 24px;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 10px;
          }
          .subtitle {
            color: #6b7280;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <img src="${qrCodeUrl}" class="qr-image" alt="QR Code" />
          <img src="/dflogo.png" class="logo" alt="Logo" />
          <div class="brand">Dinerspoint</div>
          <div class="subtitle">Scan to view our menu</div>
        </div>
        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          }
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  };

  const handleGenerateQR = useCallback(async () => {
    if (!restaurant_id) {
      setError("Error generating QR code");
      return;
    }

    setIsLoading(true);
    setError("");
    setQrCodeUrl("");

    try {
      const response = await apiClient.post(
        `/menu/generate-qr-code`,
        { restaurant_id: Number(restaurant_id) },
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
            Accept: "image/png",
          },
        }
      );
      const contentType = response.headers["content-type"];

      if (contentType.includes("application/json")) {
        const errorResponse = await response.data.text();
        const jsonError = JSON.parse(errorResponse);

        if (jsonError.statusText === "NO_MENU_ITEMS") {
          setError("Please create a menu first.");
        } else {
          setError("Failed to generate QR code. Please try again.");
        }
      } else if (contentType.includes("image/png")) {
        const url = URL.createObjectURL(
          new Blob([response.data], { type: "image/png" })
        );
        setQrCodeUrl(url);
      } else {
        setError("Failed to generate QR code. Please try again.");
      }
    } catch (error) {
      setError("Failed to generate QR code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [restaurant_id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-4 px-2">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 p-2 text-white text-center">
            <h2 className="text-4xl font-bold mb-4">Scan to Explore Our Menu</h2>
            <p className="text-purple-100 text-lg">
              Generate and download your restaurant QR code for customers
            </p>
          </div>

          <div className="p-8">
            {error && (
              <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-600 font-semibold text-center">{error}</p>
                </div>
              </div>
            )}

            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mb-4"></div>
                <p className="text-gray-600 text-lg font-semibold">Generating QR Code...</p>
                <p className="text-gray-500 text-sm mt-2">This may take a few moments</p>
              </div>
            )}

            {qrCodeUrl && (
              <div className="space-y-8">
                <div className="flex flex-col items-center bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-2xl shadow-lg border border-gray-100">
                  <div className="bg-white p-6 rounded-2xl shadow-inner border border-gray-200 mb-6">
                    <img
                      ref={qrImageRef}
                      src={qrCodeUrl}
                      alt="QR Code"
                      className="max-w-[280px] rounded-xl shadow-md"
                    />
                  </div>

                  <div className="text-center">
                    <img
                      src="/dflogo.png"
                      alt="Logo"
                      className="w-20 h-20 object-contain mx-auto mb-4"
                    />
                    <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                      Dinerspoint
                    </p>
                    <p className="text-gray-600 mt-2">Scan to view our menu</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <button
                    onClick={() => {
                      const canvas = qrCanvasRef.current;
                      const ctx = canvas.getContext("2d");

                      const qrImg = qrImageRef.current;
                      const logoImg = new Image();
                      logoImg.src = "/dflogo.png";

                      logoImg.onload = () => {
                        // White background
                        ctx.fillStyle = "#ffffff";
                        ctx.fillRect(0, 0, canvas.width, canvas.height);

                        // Draw QR image
                        ctx.drawImage(qrImg, 50, 30, 300, 300);

                        // Draw logo
                        ctx.drawImage(logoImg, 170, 350, 60, 60);

                        // Draw text
                        ctx.fillStyle = "#333333";
                        ctx.font = "bold 24px Arial";
                        ctx.textAlign = "center";
                        ctx.fillText("Dinerspoint", canvas.width / 2, 440);

                        // Trigger download
                        const link = document.createElement("a");
                        link.download = `qr_code_${restaurant_id}.png`;
                        link.href = canvas.toDataURL("image/png");
                        link.click();
                      };
                    }}
                    className="inline-flex justify-center items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <svg
                      className="w-6 h-6 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Download QR Code
                  </button>

                  <button
                    onClick={handlePrint}
                    className="inline-flex justify-center items-center px-8 py-4 bg-gradient-to-r from-gray-700 to-gray-900 text-white rounded-xl font-semibold hover:from-gray-800 hover:to-black transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <svg
                      className="w-6 h-6 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                      />
                    </svg>
                    Print QR Code
                  </button>
                </div>
              </div>
            )}

            {!qrCodeUrl && !isLoading && !error && (
              <div className="text-center py-12">
                <button
                  onClick={handleGenerateQR}
                  className="inline-flex justify-center items-center px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate QR Code
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeScanning;